import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import surveyService from '../services/surveyService';
import SurveyQuestion from '../components/SurveyQuestion';

const SurveyTaker = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [answers, setAnswers] = useState({});
  const [responseId, setResponseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadSurvey();
  }, [id]);

  useEffect(() => {
    if (survey && !survey.userCompleted) {
      loadQuestions();
    }
  }, [survey, currentPage]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await surveyService.getSurveyById(id);
      const surveyData = response.data;
      
      if (surveyData.userCompleted) {
        setSurvey(surveyData);
        setLoading(false);
        return;
      }
      
      setSurvey(surveyData);
      
      // Start survey response
      const startResponse = await surveyService.startSurvey(id);
      setResponseId(startResponse.data.responseId);
      
    } catch (error) {
      console.error('Error loading survey:', error);
      if (error.response?.status === 401) {
        setError('You need to be logged in to take this survey.');
      } else if (error.response?.status === 404) {
        setError('Survey not found or no longer available.');
      } else {
        setError('Failed to load survey. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await surveyService.getSurveyQuestions(id, currentPage);
      const data = response.data;
      
      setQuestions(data.questions);
      setTotalPages(data.totalPages);
      
    } catch (error) {
      console.error('Error loading questions:', error);
      setError('Failed to load questions. Please try again.');
    }
  };

  const handleAnswerChange = (questionId, value, selectedOptions = []) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { value, selectedOptions }
    }));
    
    // Clear validation error for this question
    if (validationErrors[questionId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const validateCurrentPage = () => {
    const errors = {};
    let isValid = true;

    questions.forEach(question => {
      const answer = answers[question.id];
      const value = answer?.value;
      const selectedOptions = answer?.selectedOptions || [];

      if (!surveyService.validateAnswer(question, value, selectedOptions)) {
        errors[question.id] = 'This field is required';
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const saveCurrentPage = async () => {
    if (!validateCurrentPage()) {
      setError('Please answer all required questions before continuing.');
      return false;
    }

    try {
      setSaving(true);
      setError('');

      const answersToSave = questions.map(question => {
        const answer = answers[question.id];
        if (!answer) return null;

        return surveyService.createAnswerRequest(
          question.id,
          question.questionType,
          answer.value,
          answer.selectedOptions
        );
      }).filter(answer => answer !== null);

      if (answersToSave.length > 0) {
        await surveyService.saveAnswers(responseId, answersToSave);
      }

      return true;
    } catch (error) {
      console.error('Error saving answers:', error);
      setError('Failed to save answers. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (await saveCurrentPage()) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentPage(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (await saveCurrentPage()) {
      try {
        setSaving(true);
        await surveyService.completeSurvey(responseId);
        setSuccess('Survey completed successfully! Thank you for your participation.');
        
        setTimeout(() => {
          navigate('/surveys');
        }, 3000);
      } catch (error) {
        console.error('Error completing survey:', error);
        setError('Failed to complete survey. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const getProgressPercentage = () => {
    if (totalPages === 0) return 0;
    return ((currentPage + 1) / totalPages) * 100;
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading survey...</p>
      </Container>
    );
  }

  if (!survey) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">Survey not found.</Alert>
      </Container>
    );
  }

  if (survey.userCompleted) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Body className="text-center">
                <div className="mb-4">
                  <i className="fas fa-check-circle fa-4x text-success"></i>
                </div>
                <h3>Survey Already Completed</h3>
                <p className="lead">You have already completed this survey.</p>
                <p>Thank you for your participation!</p>
                <Button variant="primary" onClick={() => navigate('/surveys')}>
                  View Other Surveys
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (success) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Body className="text-center">
                <div className="mb-4">
                  <i className="fas fa-check-circle fa-4x text-success"></i>
                </div>
                <h3>Survey Completed!</h3>
                <p className="lead">{success}</p>
                <p>You will be redirected shortly...</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          {/* Survey Header */}
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">{survey.title}</h4>
                  {survey.description && (
                    <small className="text-muted">{survey.description}</small>
                  )}
                </div>
                <div className="text-end">
                  <small className="text-muted">
                    Page {currentPage + 1} of {totalPages}
                  </small>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="py-2">
              <ProgressBar 
                now={getProgressPercentage()} 
                label={`${Math.round(getProgressPercentage())}%`}
                variant="success"
              />
            </Card.Body>
          </Card>

          {/* Error/Success Messages */}
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Questions */}
          <Card>
            <Card.Body>
              {questions.length === 0 ? (
                <div className="text-center py-5">
                  <p>No questions available for this page.</p>
                </div>
              ) : (
                <div className="questions-container">
                  {questions.map((question, index) => (
                    <div key={question.id} className="mb-4">
                      <SurveyQuestion
                        question={question}
                        value={answers[question.id]?.value}
                        selectedOptions={answers[question.id]?.selectedOptions || []}
                        onChange={(value, selectedOptions) => 
                          handleAnswerChange(question.id, value, selectedOptions)
                        }
                        error={validationErrors[question.id]}
                        questionNumber={currentPage * survey.questionsPerPage + index + 1}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Navigation */}
          <Card className="mt-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  {currentPage > 0 && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={handlePrevious}
                      disabled={saving}
                    >
                      <i className="fas fa-chevron-left me-2"></i>
                      Previous
                    </Button>
                  )}
                </div>
                
                <div className="text-center">
                  {saving && (
                    <div className="d-flex align-items-center">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>Saving...</span>
                    </div>
                  )}
                </div>
                
                <div>
                  {currentPage < totalPages - 1 ? (
                    <Button 
                      variant="primary" 
                      onClick={handleNext}
                      disabled={saving}
                    >
                      Next
                      <i className="fas fa-chevron-right ms-2"></i>
                    </Button>
                  ) : (
                    <Button 
                      variant="success" 
                      onClick={handleSubmit}
                      disabled={saving}
                    >
                      <i className="fas fa-check me-2"></i>
                      Submit Survey
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SurveyTaker;